import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminTrialSettingsPage() {
  const [settings, setSettings] = useState({
    days: 3,
    devices: 3,
    traffic_limit_bytes: 0,
    enabled: true,
    title_ru: 'Получите {days} дней премиум',
    title_ua: 'Отримайте {days} днів преміум',
    title_en: 'Get {days} Days Premium',
    title_cn: '获得 {days} 天高级版',
    description_ru: 'Дадим полный доступ без ограничений — протестируйте сеть перед оплатой.',
    description_ua: 'Дамо повний доступ без обмежень — протестуйте мережу перед оплатою.',
    description_en: 'We\'ll give you full access without restrictions — test the network before payment.',
    description_cn: '我们将为您提供无限制的完全访问权限 — 在付款前测试网络。',
    button_text_ru: '🎁 Попробовать бесплатно ({days} дня)',
    button_text_ua: '🎁 Спробувати безкоштовно ({days} дні)',
    button_text_en: '🎁 Try Free ({days} Days)',
    button_text_cn: '🎁 免费试用 ({days} 天)',
    activation_message_ru: '✅ Триал активирован! Вам добавлено {days} дней премиум-доступа.',
    activation_message_ua: '✅ Тріал активовано! Вам додано {days} днів преміум-доступу.',
    activation_message_en: '✅ Trial activated! You have been added {days} days of premium access.',
    activation_message_cn: '✅ 试用已激活！您已获得 {days} 天的高级访问权限。'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const { token } = useAuth();
  
  // Загрузка текущих настроек
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/admin/trial-settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Не удалось загрузить настройки');
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          ...data
        }));
      } catch (e) { 
        setError(e.message); 
      } finally { 
        setLoading(false); 
      }
    };
    
    if (token) {
      fetchSettings();
    }
  }, [token]);

  // Обработчик изменения полей
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (name.includes('bytes') ? parseInt(value, 10) : parseInt(value, 10)) : value)
    }));
  };

  // Сохранение настроек
  const handleSave = async (e) => {
    e.preventDefault();
    setSaveStatus('saving');
    setError(null);
    try {
      const response = await fetch('/api/admin/trial-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка сохранения');
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);

    } catch (e) {
      setError(e.message);
      setSaveStatus('error');
    }
  };
  
  // Форматирование трафика
  const formatTraffic = (bytes) => {
    if (bytes === 0) return 'Безлимит';
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  };

  const handleTrafficChange = (e) => {
    const value = e.target.value;
    if (value === '' || value === '0') {
      setSettings(prev => ({ ...prev, traffic_limit_bytes: 0 }));
    } else {
      const gb = parseFloat(value);
      const bytes = Math.round(gb * 1024 * 1024 * 1024);
      setSettings(prev => ({ ...prev, traffic_limit_bytes: bytes }));
    }
  };

  if (loading) {
    return (
      <main className="admin-page-content">
        <section><div className="loading-mini">Загрузка...</div></section>
      </main>
    );
  }

  const languages = [
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ua', name: 'Українська', flag: '🇺🇦' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'cn', name: '中文', flag: '🇨🇳' }
  ];

  return (
    <main className="admin-page-content">
      <section>
        <h2>Настройки триального периода</h2>
        <p className="admin-description-text">
          Здесь вы можете настроить параметры триального периода: количество дней, устройств, лимит трафика и тексты, которые будут отображаться пользователям в боте, мини-приложении и на сайте.
        </p>
        
        {error && <div className="message-error">{error}</div>}
        
        <form className="tariff-form" onSubmit={handleSave}>
          {/* Основные параметры */}
          <div className="form-section" style={{ marginBottom: '30px', padding: '20px', background: 'var(--glass-bg)', borderRadius: '12px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Основные параметры</h3>
            
            <div className="form-row">
              <div className="form-group" style={{ flex: '1 1 30%' }}>
                <label>Количество дней триала *</label>
                <input
                  type="number"
                  name="days"
                  value={settings.days}
                  onChange={handleChange}
                  min="1"
                  max="365"
                  required
                />
                <small>Количество дней бесплатного доступа</small>
              </div>
              
              <div className="form-group" style={{ flex: '1 1 30%' }}>
                <label>Количество устройств *</label>
                <input
                  type="number"
                  name="devices"
                  value={settings.devices}
                  onChange={handleChange}
                  min="1"
                  max="100"
                  required
                />
                <small>Максимальное количество устройств</small>
              </div>
              
              <div className="form-group" style={{ flex: '1 1 30%' }}>
                <label>Лимит трафика (GB)</label>
                <input
                  type="number"
                  value={settings.traffic_limit_bytes === 0 ? '' : (settings.traffic_limit_bytes / (1024 * 1024 * 1024)).toFixed(2)}
                  onChange={handleTrafficChange}
                  min="0"
                  step="0.01"
                  placeholder="Безлимит"
                />
                <small>Оставьте пустым для безлимитного трафика (текущее: {formatTraffic(settings.traffic_limit_bytes)})</small>
              </div>
            </div>
            
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="enabled"
                  checked={settings.enabled}
                  onChange={handleChange}
                />
                Триал включен
              </label>
              <small>Если отключено, кнопка триала не будет отображаться</small>
            </div>
          </div>

          {/* Тексты для разных языков */}
          {languages.map(lang => (
            <div key={lang.code} className="form-section" style={{ marginBottom: '30px', padding: '20px', background: 'var(--glass-bg)', borderRadius: '12px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px' }}>{lang.flag} {lang.name}</h3>
              
              <div className="form-group">
                <label>Заголовок</label>
                <input
                  type="text"
                  name={`title_${lang.code}`}
                  value={settings[`title_${lang.code}`] || ''}
                  onChange={handleChange}
                  placeholder="Получите {days} дней премиум"
                />
                <small>Используйте {"{days}"} для подстановки количества дней</small>
              </div>
              
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  name={`description_${lang.code}`}
                  value={settings[`description_${lang.code}`] || ''}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Дадим полный доступ без ограничений..."
                />
                <small>Текст, который будет отображаться под заголовком</small>
              </div>
              
              <div className="form-group">
                <label>Текст кнопки</label>
                <input
                  type="text"
                  name={`button_text_${lang.code}`}
                  value={settings[`button_text_${lang.code}`] || ''}
                  onChange={handleChange}
                  placeholder="🎁 Попробовать бесплатно ({days} дня)"
                />
                <small>Используйте {"{days}"} для подстановки количества дней</small>
              </div>
              
              <div className="form-group">
                <label>Сообщение при активации</label>
                <textarea
                  name={`activation_message_${lang.code}`}
                  value={settings[`activation_message_${lang.code}`] || ''}
                  onChange={handleChange}
                  rows="2"
                  placeholder="✅ Триал активирован! Вам добавлено {days} дней..."
                />
                <small>Сообщение, которое увидит пользователь после активации триала. Используйте {"{days}"} для подстановки</small>
              </div>
            </div>
          ))}

          {/* Кнопка сохранения */}
          <div className="form-actions" style={{ marginTop: '30px' }}>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? 'Сохранение...' : saveStatus === 'success' ? '✅ Сохранено!' : 'Сохранить настройки'}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}

export default AdminTrialSettingsPage;
